import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EVENTS, Joyride, STATUS } from 'react-joyride';
import type { EventHandler, Step } from 'react-joyride';
import { TourTooltip } from './TourTooltip';
import { filterValidSteps, useJoyrideOptions } from './tourUtils';

interface NewProjectTourProps {
  open: boolean;
  onComplete: () => void;
  onSkip: () => void;
}

/** Delay (ms) after `open` flips true before resolving anchors — the create
 * form renders synchronously, so a short delay just lets the page settle
 * before anchors are queried (mirrors ProjectPlayerTour's pattern). */
const ANCHOR_MOUNT_DELAY = 100;

export const NewProjectTour = ({ open, onComplete, onSkip }: NewProjectTourProps) => {
  const { t } = useTranslation('common');
  const joyrideOptions = useJoyrideOptions();
  const [steps, setSteps] = useState<Step[]>([]);

  useEffect(() => {
    if (!open) {
      setSteps([]);
      return;
    }

    const timer = window.setTimeout(() => {
      // Single-column form — the same 3 steps work at all breakpoints,
      // so no mobile/desktop split is needed.
      const tourSteps: Step[] = [
        {
          target: '[data-tour="project-name"]',
          title: t('newProjectTour.step1.title', 'Name Your Project'),
          content: t(
            'newProjectTour.step1.description',
            'Give your project a name that describes what you want to learn — for example "Spanish News Podcasts".'
          ),
        },
        {
          target: '[data-tour="project-language"]',
          title: t('newProjectTour.step2.title', "Language You're Learning"),
          content: t(
            'newProjectTour.step2.description',
            'Choose the language you want to learn. Episodes, transcriptions, and vocabulary in this project will use this language.'
          ),
        },
        {
          target: '[data-tour="user-language"]',
          title: t('newProjectTour.step3.title', 'Your Native Language'),
          content: t(
            'newProjectTour.step3.description',
            'Choose the language you speak. Translations will be generated into this language so you can check your understanding.'
          ),
        },
      ];

      setSteps(filterValidSteps(tourSteps));
    }, ANCHOR_MOUNT_DELAY);

    return () => window.clearTimeout(timer);
  }, [open, t]);

  const handleEvent: EventHandler = (data) => {
    if (data.type !== EVENTS.TOUR_END) {
      return;
    }
    if (data.status === STATUS.FINISHED) {
      // Persists new_project_tour_completed via useNewProjectTour
      onComplete();
    } else if (data.status === STATUS.SKIPPED) {
      // Persists new_project_tour_skipped (tour re-offers next visit)
      onSkip();
    }
  };

  // If no anchors survived filtering, render nothing without completing —
  // the tour retries on next mount when the anchors exist.
  if (!open || steps.length === 0) {
    return null;
  }

  return (
    <Joyride
      run
      continuous
      steps={steps}
      tooltipComponent={TourTooltip}
      onEvent={handleEvent}
      options={{
        ...joyrideOptions,
        blockTargetInteraction: true,
        closeButtonAction: 'skip',
        overlayClickAction: false,
      }}
    />
  );
};
